'use client';

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[] | string[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'brown';
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  onSelect,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  variant = 'default'
}) => {
  // Normalize options to DropdownOption format
  const normalizedOptions: DropdownOption[] = options.map(option => 
    typeof option === 'string' 
      ? { value: option, label: option }
      : option
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || "");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionValue: string) => {
    setSelectedValue(optionValue);
    setIsOpen(false);
    if (onChange) {
      onChange(optionValue);
    }
    if (onSelect) {
      onSelect(optionValue);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update selected value when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const selectedOption = normalizedOptions.find(option => option.value === selectedValue);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const getBackgroundColor = () => {
    if (variant === 'brown') {
      return 'bg-[#3D1C1A]/[0.17]';
    }
    return 'bg-global-7';
  };

  return (
    <div data-component-id="src/components/ui/Dropdown.tsx:93:4" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="93" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`} ref={dropdownRef}>
      <button data-component-id="src/components/ui/Dropdown.tsx:94:6" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="94" data-component-file="Dropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-4 py-2 h-[40px] text-left rounded-[10px]
          flex items-center justify-between
          ${getBackgroundColor()}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-25 cursor-pointer'}
        `}
      >
        <span data-component-id="src/components/ui/Dropdown.tsx:105:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="105" data-component-file="Dropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-global-1%20font-tt-norms-medium%20text-sm%22%7D" className="text-global-1 font-tt-norms-medium text-sm">
          {displayValue}
        </span>
        <Image data-component-id="src/components/ui/Dropdown.tsx:108:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="108" data-component-file="Dropdown.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_arrowdown.svg%22%2C%22alt%22%3A%22Arrow%22%7D"
          src="/images/img_arrowdown.svg"
          alt="Arrow"
          width={16}
          height={16}
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div data-component-id="src/components/ui/Dropdown.tsx:118:8" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="118" data-component-file="Dropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20z-50%20w-full%20mt-1%20bg-global-10%20border%20border-global-2%20rounded-%5B10px%5D%20shadow-lg%20max-h-60%20overflow-auto%22%7D" className="absolute z-50 w-full mt-1 bg-global-10 border border-global-2 rounded-[10px] shadow-lg max-h-60 overflow-auto">
          {normalizedOptions.map((option) => (
            <button data-component-id="src/components/ui/Dropdown.tsx:120:12" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="120" data-component-file="Dropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
              key={option.value}
              type="button"
              onClick={() => handleOptionClick(option.value)}
              className={`
                w-full px-4 py-2 text-left hover:bg-global-8 transition-colors duration-150
                ${selectedValue === option.value ? 'bg-global-9 text-global-1' : 'text-global-1'}
              `}
            >
              <span data-component-id="src/components/ui/Dropdown.tsx:129:14" data-component-path="src/components/ui/Dropdown.tsx" data-component-line="129" data-component-file="Dropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%22%7D" className="font-tt-norms-medium text-sm">
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;